import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { isEqual, noop } from 'vet-bones/utils';

import {
  MAX_CSV_PARSE_LINES,
  MAX_PROJECT_UPLOAD_SOFT_LIMIT,
} from 'src/constants/limits';
import { projectSelector } from 'src/selectors/project';
import {
  ProjectFileUploadField,
  ProjectFileUploadOptions,
  ProjectFileUploadStatistics,
} from 'src/store/state/projects';
import { assignDistinctAndRecordStatistics } from 'src/utils/csv/assignDistinctAndRecordStatistics';
import {
  ExtendedParseError,
  ParseCsvResult,
  parseCsv,
} from 'src/utils/csv/parseCsv';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { calculateProjectSize } from 'src/utils/project/calculateProjectSize';

export const useProjectFileUploadParser = (
  file: string | File | null,
  fileOptions: ProjectFileUploadOptions,
  limitRows?: number,
  existingFields?: ProjectFileUploadField[]
) => {
  const [errors, setErrors] = React.useState<ExtendedParseError[]>([]);
  const [fields, setFields] = React.useState<ProjectFileUploadField[]>(
    existingFields || []
  );
  const [rows, setRows] = React.useState<string[][]>([]);

  const parsingIds = React.useRef<Set<string>>(new Set());
  const [isParsing, setIsParsing] = React.useState(false);

  const didCleanup = React.useRef<boolean>(false);
  const abortParser = React.useRef<() => void>(noop);

  // Stabilize existingFields to prevent unnecessary re-runs
  const existingFieldsRef = React.useRef<ProjectFileUploadField[]>();
  if (!isEqual(existingFieldsRef.current, existingFields)) {
    existingFieldsRef.current = existingFields;
  }

  React.useEffect(() => {
    didCleanup.current = false;
    abortParser.current();

    if (file && fileOptions.quote && fileOptions.separator) {
      const id = uuid();
      parsingIds.current.add(id);
      setIsParsing(true);
      const { abort, parsePromise } = parseCsv<ProjectFileUploadStatistics>(
        file,
        {
          delimiter: fileOptions.separator,
          escapeChar: fileOptions.escape,
          header: fileOptions.header,
          quoteChar: fileOptions.quote,
        },
        { distinct: 0, records: 0 },
        assignDistinctAndRecordStatistics,
        limitRows
      );

      abortParser.current = abort;
      parsePromise.then(({ data, errors }) => {
        if (didCleanup.current) {
          return;
        }

        if (errors) {
          setErrors(errors);
          setFields([]);
          setRows([]);
        } else if (data) {
          setErrors([]);
          setFields(
            data.fields.map((newField, index) => ({
              ...newField,
              label:
                existingFieldsRef.current?.[index]?.value === newField.value
                  ? existingFieldsRef.current[index].label
                  : newField.label,
            }))
          );
          setRows(data.rows);
        }
        parsingIds.current.delete(id);
        setIsParsing(Boolean(parsingIds.current.size));
      });
    } else if (!didCleanup.current) {
      abortParser.current = noop;
      setFields([]);
      setRows([]);
    }

    return () => {
      didCleanup.current = true;
      abortParser.current();
    };
  }, [file, fileOptions, limitRows]);

  const updateFieldLabel = React.useCallback(
    (value: string, label: string) => {
      setFields(
        fields.map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        }))
      );
    },
    [fields]
  );

  return {
    abortParser,
    errors,
    fields,
    isParsing,
    rows,
    updateFieldLabel,
  };
};

export const useProjectMultipleFileUploadsParser = (
  files: File[],
  fileOptions: ProjectFileUploadOptions,
  maxFileSize: number | null = null
) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const [errorsByFileIdx, setErrorsByFileIdx] = React.useState<
    Record<number, ExtendedParseError[]>
  >({});
  const [fieldsByFileIdx, setFieldsByFileIdx] = React.useState<
    Record<number, ProjectFileUploadField[]>
  >({});
  const [isPartialByFileIdx, setIsPartialByFileIdx] = React.useState<
    Record<number, boolean>
  >({});
  const [rowsByFileIdx, setRowsByFileIdx] = React.useState<
    Record<number, string[][]>
  >({});

  const parsingIds = React.useRef<Set<string>>(new Set());
  const [isParsing, setIsParsing] = React.useState(false);

  const didCleanup = React.useRef<boolean>(false);
  const abortParserFns = React.useRef<Array<() => void>>([]);
  const didFilesChange = useDidValueChange(files, isEqual);
  const didOptionsChange = useDidValueChange(fileOptions, isEqual);

  React.useEffect(() => {
    didCleanup.current = false;
    abortParserFns.current.forEach((abortParser) => abortParser());

    if (didFilesChange || didOptionsChange) {
      if (files.length && fileOptions.quote && fileOptions.separator) {
        const id = uuid();
        parsingIds.current.add(id);
        setIsParsing(true);

        let loopedFileSizes = 0;
        const parsePromises: Array<
          Promise<ParseCsvResult<ProjectFileUploadStatistics>>
        > = [];
        const nextIsPartialByFileIdx: Record<number, boolean> = {};
        const currentProjectSize = calculateProjectSize(project);
        files.forEach((file, idx) => {
          const doesFileExceedMaxSize = maxFileSize && file.size > maxFileSize;
          const doesFilePutProjectOverLimit =
            currentProjectSize + loopedFileSizes + file.size >=
            MAX_PROJECT_UPLOAD_SOFT_LIMIT;
          const isFileTooBigForProject =
            doesFileExceedMaxSize || doesFilePutProjectOverLimit;

          // this somewhat sucks, but until we parse the file (in the promises below), we cannot know the size of the
          // first 25 rows. But it is likely that the size will be relatively negligible (the file likely has too many
          // rows rather than characters per row). So we treat partial files as having 0 size here...
          if (!isFileTooBigForProject) {
            loopedFileSizes += file.size;
            nextIsPartialByFileIdx[idx] = false;
          } else {
            nextIsPartialByFileIdx[idx] = true;
          }

          const { abort, parsePromise } = parseCsv<ProjectFileUploadStatistics>(
            file,
            {
              delimiter: fileOptions.separator,
              escapeChar: fileOptions.escape,
              header: fileOptions.header,
              quoteChar: fileOptions.quote,
            },
            { distinct: 0, records: 0 },
            assignDistinctAndRecordStatistics,
            isFileTooBigForProject ? MAX_CSV_PARSE_LINES : undefined
          );
          abortParserFns.current.push(abort);
          parsePromises.push(parsePromise);
        });

        Promise.all(parsePromises).then((results) => {
          const nextErrorsByFileIdx: Record<number, ExtendedParseError[]> = {};
          const nextFieldsByFileIdx: Record<number, ProjectFileUploadField[]> =
            {};
          const nextRowsByFileIdx: Record<number, string[][]> = {};
          results.forEach(({ data, errors }, idx) => {
            if (didCleanup.current) {
              return;
            }
            if (errors) {
              // this looks dumb, but it looks like papaparse reuses the same ref for
              // data && errors (so previous errors get cleared if we use the errors arr directly)
              nextErrorsByFileIdx[idx] = [...errors];
              nextFieldsByFileIdx[idx] = [];
              nextRowsByFileIdx[idx] = [];
            } else if (data) {
              nextErrorsByFileIdx[idx] = [];
              nextFieldsByFileIdx[idx] = data.fields;
              nextRowsByFileIdx[idx] = data.rows;
            }
          });
          setErrorsByFileIdx(nextErrorsByFileIdx);
          setFieldsByFileIdx(nextFieldsByFileIdx);
          setIsPartialByFileIdx(nextIsPartialByFileIdx);
          setRowsByFileIdx(nextRowsByFileIdx);
          parsingIds.current.delete(id);
          setIsParsing(Boolean(parsingIds.current.size));
        });
      } else {
        abortParserFns.current = [];
        setFieldsByFileIdx({});
        setRowsByFileIdx({});
        setIsPartialByFileIdx({});
      }
    }

    return () => {
      didCleanup.current = true;
      abortParserFns.current.forEach((abortParser) => abortParser());
    };
  }, [
    didFilesChange,
    didOptionsChange,
    errorsByFileIdx,
    fieldsByFileIdx,
    fileOptions.escape,
    fileOptions.header,
    fileOptions.quote,
    fileOptions.separator,
    files,
    maxFileSize,
    project,
    rowsByFileIdx,
    setErrorsByFileIdx,
  ]);

  const updateFieldLabelForFileIdx = React.useCallback(
    (idx: number, value: string, label: string) => {
      if (!fieldsByFileIdx[idx]) {
        return;
      }
      setFieldsByFileIdx({
        ...fieldsByFileIdx,
        [idx]: fieldsByFileIdx[idx].map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        })),
      });
    },
    [fieldsByFileIdx]
  );

  return {
    abortParserFns: abortParserFns.current,
    errorsByFileIdx,
    fieldsByFileIdx,
    isParsing,
    isPartialByFileIdx,
    rowsByFileIdx,
    updateFieldLabelForFileIdx,
  };
};
